import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddUserIcon,
  CloseIcon,
  NoPhotoIcon,
  getModuleIcon,
  CollapseIcon,
  ConfirmDialog,
  ExpandIcon,
  SectionCard,
} from '@platform/ui-kit';
import {
  MODULE_CATALOG,
  UNLIMITED_SEATS,
} from '@platform/entitlements';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type AdminUser, type EntitlementRow, type ProductRole } from '../../api';
import { describeError, useAsyncData } from './useAsyncData';

/**
 * A status as a coloured WORD, never a chip.
 *
 * The owner's ruling, for the grid and the panel alike: "you might just keep the font in
 * green, but I don't need additional shapes around it… there's too many colours."
 */
function StatusText({ label, color }: { label: string; color: string }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   THIS SCREEN IS THE APPROVED PROTOTYPE, reproduced value for value.

   The prototype is `users-prototype.html` — the artifact the owner signed off. What
   follows keeps ITS measurements, not new ones: a plain table (not the DataGrid), and
   a panel that is an INLINE GRID COLUMN beside that table rather than an overlay.

   The inline panel is the whole point, and the reason three earlier attempts had "no
   header": a `Drawer` is a modal that sits under the shell's app bar, which paints over
   exactly the 64px strip holding Back / Maximize / Close. A column inside the page
   cannot be covered by anything.

   Not brought across: the prototype's three explanatory note cards at the foot of the
   page, per the owner — those document the change, they are not the screen.
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Where a photograph would go.
 *
 * The owner's ruling (2026-08-08): the coloured initials discs are out — "find out the
 * icon that represents a photo, but photo has not been provided."
 *
 * `NoPhotoIcon` is `AccountCircleOutlined`, the silhouette-in-a-circle everyone already
 * reads as "no profile picture". Coloured initials said the initials were the point and
 * gave every row a different accent, which competed with the module badges and the status
 * word — the only two things on the row that carry meaning through colour. A single grey
 * placeholder says a photo is MISSING and then gets out of the way.
 *
 * `name` is kept for the tooltip and `id` for the call sites, so wiring real photographs
 * later is a change to this component alone.
 */
function Avatar({ name, size = 30 }: { name: string; id?: number; size?: number }) {
  return (
    <NoPhotoIcon
      titleAccess={`${name} — no photo`}
      sx={{ width: size, height: size, flex: 'none', color: '#b0bec5' }}
    />
  );
}

/** The prototype's `MOD_C`: a short badge per product, keyed by the catalogue's own id. */
const MOD_C: Record<string, { label: string; color: string }> = {
  portfolio_definition: { label: 'OR', color: '#7b1fa2' },
  project_management: { label: 'PM', color: '#ef6c00' },
  apr: { label: 'APR', color: '#1565c0' },
  spn: { label: 'SPN', color: '#00838f' },
  ai_readiness: { label: 'AIR', color: '#2e7d32' },
  erp_consolidation: { label: 'ERP', color: '#ad1457' },
  // The catalogue calls this one "RFx Desk"; its id is a leftover from an earlier name.
  // Badged from the PRODUCT NAME, never the id — truncating the id rendered "STR".
  strategic_intelligence: { label: 'RFx', color: '#c62828' },
  program_planning: { label: 'PP', color: '#5e35b1' },
};

/*
  Every catalogued product has an entry above. The fallback initials the product's NAME
  rather than its id, so a new module reads as something recognisable instead of the first
  three characters of a database key.
*/
const modBadge = (key: string) => {
  const known = MOD_C[key];
  if (known) return known;
  const name = MODULE_CATALOG.find((m) => m.id === key)?.name;
  const label = name
    ? name
        .split(/[\s&]+/)
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 3)
        .join('')
        .toUpperCase()
    : key.slice(0, 3).toUpperCase();
  return { label, color: '#607d8b' };
};

function ModBadge({ moduleKey, title }: { moduleKey: string; title?: string }) {
  const { label, color } = modBadge(moduleKey);
  return (
    <Tooltip title={title ?? ''} disableHoverListener={!title}>
      <Box
        component="span"
        sx={{
          fontSize: 10,
          fontWeight: 700,
          borderRadius: '4px',
          padding: '1px 6px',
          border: '1px solid currentColor',
          whiteSpace: 'nowrap',
          color,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

/** locked ▸ inactive ▸ active, exactly as the prototype's `statusOf`. */
const statusOf = (u: AdminUser) => (u.is_locked === 1 ? 'locked' : u.is_active === 1 ? 'active' : 'inactive');

const STATUS_COLOR: Record<string, string> = {
  active: '#2e7d32',
  locked: '#d32f2f',
  inactive: '#90a4ae',
};

/** `.p-sec` — the small caps label above each panel block. */
const SECTION_LABEL = {
  fontSize: '9.5px',
  fontWeight: 800,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: '#90a4ae',
  margin: '0 0 8px',
} as const;

/** Table cell padding and rules — `tbody td` in the prototype. */
const TD: React.CSSProperties = {
  padding: '9px 14px',
  borderBottom: '1px solid #eef2f6',
  fontSize: 13.5,
  verticalAlign: 'middle',
};

/** `thead th` in the prototype. */
const TH: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  color: '#90a4ae',
  padding: '9px 14px',
  background: '#eef2f6',
  borderBottom: '1.5px solid #e0e6ea',
  whiteSpace: 'nowrap',
};

/**
 * Row actions — Application Master Data's own buttons, value for value.
 *
 * Taken from `master-data/ApplicationMasterDataTab.tsx` (`ACTION_BTN` / `DANGER_BTN`):
 * a bordered, transparent button carrying an EMOJI glyph, not an MUI icon. That screen is
 * the platform's canon for a data grid and this one now matches it — 👁️ view, ✏️ edit,
 * 🗑️ delete, and the same treatment on the panel's Lock / Reset / Deactivate.
 */
const ACTION_BTN: React.CSSProperties = {
  padding: '4px 6px',
  borderRadius: '4px',
  border: '1px solid #1976d2',
  color: '#1976d2',
  background: 'transparent',
  fontSize: 14,
  lineHeight: 1,
  cursor: 'pointer',
};
const DANGER_BTN: React.CSSProperties = { ...ACTION_BTN, borderColor: '#d32f2f', color: '#d32f2f' };

/** The panel action buttons: same border, with a label beside the glyph. */
/** A panel action that is not available right now — Edit, while already editing. */
const DISABLED_BTN: React.CSSProperties = {
  borderColor: '#cfd8dc',
  color: '#b0bec5',
  cursor: 'default',
};

const PANEL_BTN: React.CSSProperties = {
  ...ACTION_BTN,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  padding: '8px 10px',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
};

/**
 * Panel header controls, from `master-data/ApplicationDetailDrawer.tsx`.
 *
 * 36px tall, radius 9, 1.5px border. Back and maximize are slate; CLOSE IS RED — that is
 * the drawer's own treatment and the detail this screen was missing.
 */
const CONTROL_BASE = {
  height: 36,
  borderRadius: '9px',
  border: '1.5px solid',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  font: 'inherit',
} as const;
const BACK_BUTTON_SX = {
  ...CONTROL_BASE,
  px: '14px',
  gap: '4px',
  borderColor: '#cbd5e1',
  bgcolor: '#f1f5f9',
  color: '#334155',
  fontSize: 13,
  fontWeight: 700,
} as const;
const PANEL_ICON_SX = {
  ...CONTROL_BASE,
  width: 36,
  borderColor: '#cbd5e1',
  bgcolor: '#f1f5f9',
  color: '#475569',
} as const;
const CLOSE_BUTTON_SX = {
  ...CONTROL_BASE,
  width: 36,
  borderColor: '#dc2626',
  bgcolor: '#fee2e2',
  color: '#b91c1c',
} as const;

/**
 * Status as a coloured WORD, not a chip.
 *
 * The owner's call: a filled badge per row, next to a coloured role badge and coloured
 * module chips, was three background shapes competing on every line. Green reads active,
 * red reads locked, grey reads neither — and the row is quiet again.
 */

const CLIENT_ROLES = ['Client Administrator', 'Standard User'];

/**
 * THE ASSIGNMENT RULE, as the owner stated it (2026-08-08):
 *
 *   A customer's platform role is either Client Administrator or Standard User.
 *
 *   CLIENT ADMINISTRATOR, while ACTIVE, is assigned to every application, with the
 *   Administrator role in each. Nothing is ticked by hand and no product role is chosen —
 *   both follow from the platform role.
 *
 *   STANDARD USER is assigned application by application, by hand, and for each one the
 *   role is picked from that application's own set.
 *
 * "Administrator" is DERIVED, not a row in `platform.roles`. No product seeds an
 * Administrator role — being an administrator is one fact about a person, held once at
 * platform level in `platform.users.role`. That is the same ruling APR's two roles and
 * Inventory Registry's four were seeded under.
 *
 * Held while ACTIVE. A deactivated administrator keeps the platform role and loses the
 * access, which is what `is_active` already does everywhere else.
 */
const ADMIN_ROLES = ['Client Administrator', 'Talogent Administrator'];

/** The label shown where a product role would otherwise be, for an administrator. */
const IMPLIED_ADMIN_ROLE = 'Administrator';

/**
 * How far the second line of an application row is indented.
 *
 * The checkbox's own footprint: 20px control + 8px of padding either side, plus the 8px
 * gap before the icon. Written as the sum rather than a single number so it stays right if
 * the checkbox padding changes — the dropdown lines up under the ICON, not near it.
 */
const ROW_INDENT = '44px';

/**
 * Roles offered in the user dialog.
 *
 * Talogent Administrator appears ONLY for vendor staff. A client administrator never
 * learns the role exists, and the server refuses it from a client domain anyway —
 * this is the presentation half of a boundary enforced in the API and by a database
 * CHECK.
 */
function rolesFor(isTalogent: boolean): string[] {
  return isTalogent ? [...CLIENT_ROLES, 'Talogent Administrator'] : CLIENT_ROLES;
}

/**
 * The drawer covers the three things this screen cannot say on its own: that a Client
 * Administrator's module access is DERIVED rather than ticked, that Lock and Deactivate
 * are different acts with different causes, and that a refused assignment is a seat limit
 * rather than a fault. Each of those has been asked about more than once.
 */
const USERS_HELP: HelpContent = {
  title: 'Users',
  icon: '👥',
  purpose:
    'Holds every <strong>account</strong> in this install &mdash; who exists, what they may open, and whether they can sign in at all. Giving somebody access to a module here is what <strong>consumes a licensed seat</strong>.',
  doHere: [
    'Filter the list by <strong>name or email</strong>, by <strong>platform role</strong>, by <strong>module role</strong> or by <strong>status</strong>.',
    'Open a person with the <strong>view</strong> icon to read their account beside the list, without leaving it.',
    'Add an account with <strong>Add user</strong>, and edit one with the <strong>edit</strong> icon.',
    'Give a Standard User access to a module, and pick <strong>their role within that module</strong>.',
    'From the open panel: <strong>Lock</strong>, <strong>Deactivate</strong> or <strong>Reset password</strong>.',
    'Delete an account with the <strong>delete</strong> icon &mdash; for an account created in error, not for someone who has left.',
  ],
  fits: 'Administration &rarr; Users',
  firstTime:
    'Decide the <strong>platform role</strong> first, because it decides how much else there is to do. A Client Administrator needs nothing further &mdash; their module access follows automatically. A Standard User is then given modules one at a time.',
  sections: [
    {
      type: 'defs',
      label: 'The two platform roles',
      items: [
        {
          term: 'Client Administrator',
          def: 'Runs this install. <strong>Assigned to every licensed application automatically, with the administrator role in each</strong> &mdash; nothing is ticked by hand and no module role is chosen, because both follow from the platform role. The module list shows this as already settled rather than as empty.',
        },
        {
          term: 'Standard User',
          def: 'Assigned <strong>application by application</strong>, by hand, and for each one their role is picked from that application&rsquo;s own set of roles. This is the role almost everybody has.',
        },
      ],
      note:
        'An administrator holds that access <em>while active</em>. Deactivating one keeps the platform role and removes the access.',
    },
    {
      type: 'html',
      label: 'Lock and Deactivate are not the same thing',
      html: '<strong>Locked</strong> is a security state &mdash; the account is barred, typically after failed sign-ins or while something is being investigated. Unlocking it also <strong>clears the failed-attempt count</strong>, so unlock is the fix for "they have locked themselves out", not a password reset. <strong>Inactive</strong> is a lifecycle state &mdash; the person has left, or has not started. Both stop sign-in; neither releases the person&rsquo;s module assignments, which is deliberate so that re-activating somebody restores what they had rather than making an administrator rebuild it. The status shown is <em>locked</em> ahead of <em>inactive</em> ahead of <em>active</em>, because a locked account is the more urgent fact.',
    },
    {
      type: 'html',
      label: 'When a module cannot be assigned',
      html: 'A module reading <strong>No seats left</strong> is not a fault and not a permission problem: that module&rsquo;s licence has no free seat. The limit is enforced by the server at the moment of assignment, so the screen shows you the refusal rather than quietly hiding the module. Either free a seat by removing somebody else&rsquo;s access, or the licence itself has to change &mdash; see <em>Administration &rarr; License Usage</em>, where a seat count of <strong>0</strong> means the module is unavailable even though it reads as licensed.',
    },
    {
      type: 'html',
      label: 'Deleting, versus the thing you usually want',
      html: '<strong>Deactivate</strong> is almost always the right action for a person who has left: the account stops working, the record and its history remain, and the audit trail still makes sense. <strong>Delete</strong> is for an account created in error. The platform also refuses to leave itself unreachable &mdash; it will not let you lock, deactivate or remove the <strong>last active administrator</strong>, so promote somebody else first.',
    },
    {
      type: 'html',
      label: 'Passwords',
      html: 'An administrator sets a password directly; the platform <strong>does not email credentials</strong>, so the new password has to be communicated by whatever means the client already trusts. <strong>Reset password</strong> replaces it; a stored password is never displayed back, here or anywhere else.',
    },
  ],
};

/**
 * Screen 3 — Users (spec §C).
 *
 * Rebuilt in MUI from AI Readiness's admin-users screen plus its per-project
 * permission screens. Seat limits are enforced by the server on assignment; this
 * screen surfaces the rejection rather than pre-empting it, so the limit is decided
 * in one place.
 */
export function UsersTab() {
  const session = useAsyncData(() => api.session());
  const isTalogent = session.data?.user?.canAccessTalogent ?? false;

  // Which set of accounts is on screen. Talogent staff open on their own accounts,
  // which is what they came for; the client has no switch, and the server would
  // refuse the vendor scope from them anyway.
  const [scope, setScope] = useState<'client' | 'talogent'>();
  const effectiveScope = scope ?? (isTalogent ? 'talogent' : 'client');

  const users = useAsyncData(() => api.admin.getUsers(effectiveScope), [effectiveScope]);
  const entitlements = useAsyncData(() => api.admin.getEntitlements());
  // Seeded rows, not an enum: a product that adds a role appears in the filter without a
  // front-end change. This is the same catalogue the panel's per-module picker reads.
  const productRoleCatalog = useAsyncData(() => api.admin.getProductRoles());
  /**
   * The side panel. `user: 'new'` is the add form; `editing` decides whether the panel
   * opens read-only or in edit mode, which is what makes 👁️ and ✏️ different buttons.
   */
  const [panel, setPanel] = useState<{ user: AdminUser | 'new'; editing: boolean }>();
  // Filters. Free text spans name AND email, because an administrator looking for someone
  // has one of the two and should not have to know which box it goes in.
  const [fText, setFText] = useState('');
  const [fRole, setFRole] = useState('');
  const [fProductRole, setFProductRole] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [actionError, setActionError] = useState<string>();
  /* Lifted out of the panel: it drives the GRID's columns, not just the panel's own width. */
  const [maximized, setMaximized] = useState(false);

  /*
    THE FILTER BAR IS PINNED; only the rows move.

    The table header pins DIRECTLY BENEATH it, which means it needs the bar's height —
    and that height changes with the viewport, because the filters wrap onto a second
    line when the card is narrow. So it is MEASURED, never typed as a constant: a number
    in the CSS describing another element's size is right in one state and wrong in the
    next, which is exactly how the earlier `top: 41px` / `top: 79px` rounds went wrong.
  */
  const filterBarRef = useRef<HTMLDivElement | null>(null);

  /*
    ONLY THE ROWS SCROLL.

    The shell's `<main>` has no height, so by default the whole document scrolls and the
    title, the filters and the Add-user button slide away with the rows. The fix is to give
    the body its OWN scroll region, sized to whatever is left between where that region
    starts and the bottom of the window.

    NOTHING HERE IS A TYPED CONSTANT. The region's top depends on the page header, the
    filter bar's wrapping, and the Talogent scope toggle when it is present; its bottom
    depends on the padding `AppShell` reserves for the anchored footer. All of it changes
    with the viewport, and all of it is something the browser already knows — so both
    numbers are read from the DOM rather than repeated here. See `measure()` below.
  */
  /*
    A CALLBACK REF, not `useRef`, and this is the whole bug that made the page keep
    scrolling.

    This component returns a spinner while `users`/`entitlements` load, so the table does
    not exist on first render. The effect that measures ran once with `[]` deps — at that
    moment `bodyRef.current` was null, `measure()` returned early, and the ResizeObserver
    attached to nothing. `bodyH` stayed null, no height was ever set, and the page scrolled
    exactly as before. A refresh "put it back" because it had never been applied at all.

    A callback ref fires when the node actually attaches, whenever that happens.
  */
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [bodyH, setBodyH] = useState<number | null>(null);
  useEffect(() => {
    if (!bodyEl) return;
    const main = bodyEl.closest('main');

    const measure = () => {
      const top = bodyEl.getBoundingClientRect().top;
      /*
        THE BOTTOM INSET IS READ FROM THE PAGE, not from a token.

        `AppShell`'s <main> reserves the anchored footer's band as its own padding-bottom.
        An earlier pass subtracted `LAYOUT.footerHeight` (40) where the real reservation is
        `footerHeight + 24` (64), so the document came out 16px taller than the viewport and
        the page could always scroll a little. Asking the element for its computed padding
        cannot drift: change the token, change the padding, add a banner — this follows.
      */
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(240, Math.floor(window.innerHeight - top - inset));
      // Ignore sub-pixel churn, so observing <main> cannot feed itself a resize loop.
      setBodyH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };

    measure();
    window.addEventListener('resize', measure);

    /*
      Observe EVERYTHING ABOVE the table, not just the filter bar. The region's top moves
      when the filters wrap to a second line, when the Talogent scope toggle appears, and
      when an error alert opens above the card. Watching one of the three is how this ends
      up right in one state and wrong in another.
    */
    const ro = new ResizeObserver(measure);
    if (filterBarRef.current) ro.observe(filterBarRef.current);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [bodyEl]);
  const [busy, setBusy] = useState(false);

  /**
   * What can be assigned to a person: enabled AND still in the catalogue.
   *
   * `in_catalog` matters because an entitlement row outlives the module it names. A
   * module that is dropped from the catalogue leaves its `app_entitlements` row behind
   * with its seat count intact, and without this filter that row would keep offering a
   * tickbox for an application nobody can open. The Entitlements screen still shows the
   * row, flagged "Not in catalogue", which is where an orphan belongs — visible to an
   * administrator who can clear it, not offered to whoever is editing a user.
   */
  const licensed = useMemo(
    () => (entitlements.data?.entitlements ?? []).filter((e) => e.enabled && e.in_catalog),
    [entitlements.data],
  );

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await fn();
      users.reload();
      entitlements.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const [resetTarget, setResetTarget] = useState<AdminUser>();
  const [deleteTarget, setDeleteTarget] = useState<AdminUser>();

  /**
   * The visible rows.
   *
   * Filtering happens here rather than in the grid's own filter model so the four controls
   * read as one bar and the "N of M shown" count is honest about all of them at once.
   */
  /** role_key ▸ display name, for the module badge tooltips. */
  const roleNameOf = (roleKey: string | undefined) =>
    roleKey
      ? ((productRoleCatalog.data?.roles ?? []).find((r) => r.role_key === roleKey)?.role_name ??
        roleKey)
      : undefined;

  /**
   * The column headings stick to the top of the rows' scroll box.
   *
   * `top: 0`, not a measured offset: the filter bar is now OUTSIDE that box and never
   * scrolls, so there is nothing above the headings to clear.
   */
  const stickyHead: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 2 };

  const allUsers = users.data?.users ?? [];
  const rows = allUsers.filter((u) => {
    const text = fText.trim().toLowerCase();
    if (
      text &&
      !u.full_name.toLowerCase().includes(text) &&
      !u.email.toLowerCase().includes(text)
    ) {
      return false;
    }
    if (fRole && u.role !== fRole) return false;
    if (fProductRole && !Object.values(u.productRoles ?? {}).includes(fProductRole)) return false;
    if (fStatus) {
      const state =
        u.is_active !== 1 ? 'inactive' : u.is_locked === 1 ? 'locked' : 'active';
      if (state !== fStatus) return false;
    }
    return true;
  });
  const filtered = fText || fRole || fProductRole || fStatus;

  if (users.loading || entitlements.loading) return <CircularProgress />;
  if (users.error) return <Alert severity="error">{users.error}</Alert>;

  return (
    <Stack spacing={2}>
      <HelpPanel help={USERS_HELP} />

      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(undefined)}>
          {actionError}
        </Alert>
      ) : null}

      {/*
        NO CARD HEADING. The owner's ruling (2026-08-07): the "Users · N accounts" strip
        is dropped and Add user sits in the filter bar, after the last filter. The page
        already carries the title, so the heading was saying it twice.
      */}
      <SectionCard disableContentPadding>
        {isTalogent ? (
          <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={effectiveScope}
              onChange={(_, next: 'client' | 'talogent' | null) => next && setScope(next)}
              aria-label="Which accounts to show"
            >
              <ToggleButton value="talogent">Talogent users</ToggleButton>
              <ToggleButton value="client">Client users</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}

        <Box
          ref={filterBarRef}
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'center',
            flexWrap: 'wrap',
            px: 2.5,
            py: 1.75,
            borderBottom: 1,
            borderColor: 'divider',
            // Not sticky any more: it sits above the scroll region, so it cannot move.
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            size="small"
            type="search"
            placeholder="Filter by name or email…"
            value={fText}
            onChange={(e) => setFText(e.target.value)}
            sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
          />
          {/* Platform role and module role: matched widths, so the two selects line up. */}
          <TextField
            size="small"
            select
            value={fRole}
            onChange={(e) => setFRole(e.target.value)}
            sx={{ width: 235 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All platform roles</MenuItem>
            {rolesFor(isTalogent).map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            value={fProductRole}
            onChange={(e) => setFProductRole(e.target.value)}
            sx={{ width: 235 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All module roles</MenuItem>
            {(productRoleCatalog.data?.roles ?? []).map((r) => (
              <MenuItem key={r.role_key} value={r.role_key}>
                {r.role_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            sx={{ width: 150 }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="locked">Locked</MenuItem>
          </TextField>
          {filtered ? (
            <Button
              size="small"
              onClick={() => {
                setFText('');
                setFRole('');
                setFProductRole('');
                setFStatus('');
              }}
            >
              Clear
            </Button>
          ) : null}
          {/* Count, then the button hard against the right edge. */}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {rows.length} user{rows.length === 1 ? '' : 's'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddUserIcon />}
            onClick={() => setPanel({ user: 'new', editing: true })}
          >
            Add user
          </Button>
        </Box>

        {/*
          THE PROTOTYPE'S TABLE, not the DataGrid.

          A plain table is what the approved design is, and it settles the owner's other
          ruling for free: there is no page size to cap, so every user is always on
          screen. The DataGrid could not do that — Community rejects `pagination={false}`
          and caps a page at 100.
        */}
        <Box
          ref={setBodyEl}
          sx={{
            ...(bodyH ? { height: bodyH } : {}),
            /*
              `minHeight: 0` and `overflow: hidden` are what actually confine the panel.

              A grid item's default is `min-height: auto`, which means it REFUSES to shrink
              below its own content. Without these the panel grew past this row, overflowed
              a container that was not clipping, and pushed the document taller — so opening
              a user scrolled the whole page instead of the panel. The measured height alone
              did not fix that: it sized the row, and the row was not the thing overflowing.
            */
            minHeight: 0,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: panel
              ? maximized
                ? '0 minmax(0, 1fr)'
                : 'minmax(0, 1fr) 440px'
              : 'minmax(0, 1fr) 0',
            transition: 'grid-template-columns .18s ease',
            '@media (max-width: 1100px)': {
              gridTemplateColumns: panel ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 0',
            },
          }}
        >
          <Box
            sx={{
              overflowX: 'auto',
              // The rows' own scroll. The column headings stick to the top of THIS box.
              overflowY: 'auto',
              borderTop: '1px solid #e0e6ea',
              minWidth: 0,
              minHeight: 0,
              ...(panel && maximized ? { display: 'none' } : {}),
            }}
          >
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, ...stickyHead }}>User</th>
                  <th style={{ ...TH, ...stickyHead }}>Platform role</th>
                  <th style={{ ...TH, ...stickyHead }}>Module access</th>
                  <th style={{ ...TH, ...stickyHead }}>Status</th>
                  <th style={{ ...TH, ...stickyHead }}>Last sign-in</th>
                  <th style={{ ...TH, ...stickyHead, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const st = statusOf(u);
                  const selected = !!panel && panel.user !== 'new' && panel.user.user_id === u.user_id;
                  const mods = Object.keys(u.productRoles ?? {});
                  const assigned = mods.length ? mods : u.modules;
                  /*
                    An ACTIVE administrator reaches every licensed application, so the row
                    says ALL rather than listing the seats they happen to hold. The owner
                    (2026-08-08): glucic@gmail.com "should have all those application badges
                    since he is Client Administrator, or new ALL badge."

                    ONE BADGE, NOT SEVEN. The badges say which products a person can open;
                    for an administrator the answer is every licensed one, and it changes
                    whenever a licence does. Painting a fixed row of product badges would
                    go stale the moment Entitlements changed; ALL cannot.

                    Same `adminHoldsAll` rule the panel uses — active AND an admin role, so
                    a deactivated administrator does not advertise access it no longer has.
                  */
                  const holdsAll = ADMIN_ROLES.includes(u.role) && u.is_active === 1;
                  return (
                    <Box
                      component="tr"
                      key={u.user_id}
                      onClick={() => setPanel({ user: u, editing: false })}
                      sx={{
                        cursor: 'pointer',
                        '& td': selected ? { background: '#eaf3ff' } : {},
                        '&:hover td': { background: selected ? '#eaf3ff' : '#eef2f6' },
                      }}
                    >
                      <td style={TD}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                          <Avatar name={u.full_name} id={u.user_id} />
                          <Box sx={{ minWidth: 0 }}>
                            <Box
                              sx={{
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {u.full_name}
                            </Box>
                            <Box
                              sx={{
                                fontSize: 11.5,
                                color: '#90a4ae',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {u.email}
                            </Box>
                          </Box>
                        </Box>
                      </td>
                      <td style={TD}>
                        <Box component="span" sx={{ fontSize: 13, color: '#455a64', whiteSpace: 'nowrap' }}>
                          {u.role}
                        </Box>
                      </td>
                      <td style={TD}>
                        {holdsAll ? (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              borderRadius: '4px',
                              padding: '1px 6px',
                              border: '1px solid currentColor',
                              whiteSpace: 'nowrap',
                              color: '#1565c0',
                            }}
                            title={`${u.role} — every licensed application`}
                          >
                            ALL
                          </Box>
                        ) : assigned.length ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {assigned.map((key) => (
                              <ModBadge
                                key={key}
                                moduleKey={key}
                                title={roleNameOf(u.productRoles?.[key])}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Box component="span" sx={{ color: '#90a4ae', fontSize: 12 }}>
                            —
                          </Box>
                        )}
                      </td>
                      <td style={TD}>
                        <Box
                          component="span"
                          sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: STATUS_COLOR[st] }}
                        >
                          {st[0]!.toUpperCase() + st.slice(1)}
                        </Box>
                      </td>
                      <td style={{ ...TD, color: '#90a4ae', fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        {u.last_login ? new Date(u.last_login).toLocaleString() : '—'}
                      </td>
                      <td style={TD}>
                        <Box
                          sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            title="View user"
                            aria-label="View user"
                            style={ACTION_BTN}
                            onClick={() => setPanel({ user: u, editing: false })}
                          >
                            👁️
                          </button>
                          <button
                            type="button"
                            title="Edit user"
                            aria-label="Edit user"
                            style={ACTION_BTN}
                            onClick={() => setPanel({ user: u, editing: true })}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            title="Delete user"
                            aria-label="Delete user"
                            style={DANGER_BTN}
                            onClick={() => setDeleteTarget(u)}
                          >
                            🗑️
                          </button>
                        </Box>
                      </td>
                    </Box>
                  );
                })}
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 34, textAlign: 'center', color: '#90a4ae' }}
                    >
                      No users match these filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Box>
          </Box>

          {/* THE PANEL — a column of this grid, never an overlay. */}
          {panel ? (
            <UserPanel
              /*
                The panel seeds its form state once, on mount. Without a key, clicking 👁️
                on a second row — or ✏️ on the row already open — would reuse the mounted
                instance and show the first row's values. Keying on identity+mode remounts.
              */
              key={`${panel.user === 'new' ? 'new' : panel.user.user_id}:${panel.editing ? 'e' : 'v'}`}
              user={panel.user === 'new' ? undefined : panel.user}
              startEditing={panel.editing}
              maximized={maximized}
              onToggleMaximized={() => setMaximized((m) => !m)}
              licensed={licensed}
              isTalogent={isTalogent}
              talogentScope={effectiveScope === 'talogent'}
              busy={busy}
              onClose={() => {
                setPanel(undefined);
                setMaximized(false);
              }}
              onSaved={() => {
                setPanel(undefined);
                setMaximized(false);
                users.reload();
                entitlements.reload();
              }}
              onError={setActionError}
              onResetPassword={(u: AdminUser) => setResetTarget(u)}
              onToggleLock={(u: AdminUser) =>
                void run(() =>
                  u.is_locked === 1 ? api.admin.unlockUser(u.user_id) : api.admin.lockUser(u.user_id),
                ).then(() => setPanel(undefined))
              }
              onToggleActive={(u: AdminUser) =>
                void run(() =>
                  api.admin.updateUser(u.user_id, { isActive: u.is_active !== 1 }),
                ).then(() => setPanel(undefined))
              }
            />
          ) : null}
        </Box>
      </SectionCard>

      {resetTarget ? (
        <ResetPasswordDialog
          user={resetTarget}
          onClose={() => setResetTarget(undefined)}
          onDone={() => {
            setResetTarget(undefined);
            users.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Delete ${deleteTarget?.full_name ?? ''}?`}
        message={
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {deleteTarget?.email} will be permanently removed, along with their module access
              and password history. This cannot be undone.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Any licensed seats they hold are released. Their entries in the audit log remain.
              To keep the account for the record instead, edit it and clear <strong>Active</strong>.
            </Typography>
          </>
        }
        confirmLabel="Delete user"
        destructive
        busy={busy}
        onCancel={() => setDeleteTarget(undefined)}
        onConfirm={() => {
          const target = deleteTarget;
          setDeleteTarget(undefined);
          if (target) void run(() => api.admin.deleteUser(target.user_id));
        }}
      />
    </Stack>
  );
}

/**
 * The user editor, as a RIGHT-SIDE PANEL rather than a modal.
 *
 * The owner's ruling (2026-08-07): this is the Application Master Data pattern — the list
 * stays on screen while you work, so you can move between people without closing anything.
 * A modal took the whole screen for a form that is mostly a set of tick-boxes.
 *
 * READ-ONLY UNTIL EDIT, as that drawer is: 👁️ opens it to look, ✏️ opens it to change.
 * The header carries the drawer's own controls — `‹ Back`, maximize, and the RED close.
 *
 * Lock / Deactivate / Reset password sit in the footer beside Edit, because they are things
 * you do TO the person you are looking at. DELETE IS NOT HERE: it stays a row action, so a
 * destructive step is never one mis-click away from a routine one.
 */
function UserPanel({
  user,
  startEditing,
  maximized,
  onToggleMaximized,
  licensed,
  isTalogent,
  talogentScope,
  busy,
  onClose,
  onSaved,
  onError,
  onResetPassword,
  onToggleLock,
  onToggleActive,
}: {
  user?: AdminUser;
  /** Which button opened it: ✏️ starts in edit mode, 👁️ starts read-only. */
  startEditing: boolean;
  maximized: boolean;
  onToggleMaximized: () => void;
  licensed: EntitlementRow[];
  isTalogent: boolean;
  /** Adding on the Talogent list: one role, no module assignment. */
  talogentScope: boolean;
  busy: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
  onResetPassword: (user: AdminUser) => void;
  onToggleLock: (user: AdminUser) => void;
  onToggleActive: (user: AdminUser) => void;
}) {
  const isNew = !user;
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  // A Talogent account has exactly one role and is never given module seats.
  const talogentAccount = talogentScope && !user;
  const [role, setRole] = useState(
    user?.role ?? (talogentAccount ? 'Talogent Administrator' : 'Standard User'),
  );
  const [isActive, setIsActive] = useState(user ? user.is_active === 1 : true);
  const [password, setPassword] = useState('');
  const [modules, setModules] = useState<string[]>(user?.modules ?? []);
  /*
    There is ONE kind of licence, so a grant carries no choice any more — it is derived
    from the ticked modules at save time. The server's `setUserModules` still takes a
    grants map, so the shape is built here rather than changing that contract.
  */
  const grantsFor = (keys: string[]) =>
    Object.fromEntries(keys.map((k) => [k, { type: 'permanent' as const }]));
  /**
   * The role this user holds INSIDE each product, keyed by module.
   *
   * Separate from `role` above, which is platform-wide (Talogent Administrator / Client
   * Administrator / Standard User). Being an administrator is one fact about a person;
   * what they are inside APR is a different question, and this answers it. An
   * "APR administrator" is therefore a Client Administrator who holds the APR module —
   * there is no per-product Administrator role, by the owner's ruling.
   */
  const [productRoles, setProductRoles] = useState<Record<string, string>>(
    user?.productRoles ?? {},
  );
  const [roleCatalog, setRoleCatalog] = useState<ProductRole[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  // A new user has nothing to look at, so the add form is always editable.
  const [editing, setEditing] = useState(startEditing || !user);

  // The roles each product declares. Seeded rows, not an enum in code, so a product that
  // adds a role gets a picker entry without a front-end change.
  useEffect(() => {
    let cancelled = false;
    void api.admin
      .getProductRoles()
      .then((r) => {
        if (!cancelled) setRoleCatalog(r.roles);
      })
      .catch(() => {
        /* the dialog still works without it — the picker simply does not render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rolesForModule = (key: string) => roleCatalog.filter((r) => r.module_key === key);

  const catalogName = (key: string) =>
    MODULE_CATALOG.find((m) => m.id === key)?.name ?? key;

  /** The same icon the launcher and the left nav use for this module. */
  const catalogIcon = (key: string) =>
    MODULE_CATALOG.find((m) => m.id === key)?.icon ?? key;

  const toggleModule = (key: string) => {
    setModules((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
    // Unticking a product drops its role too. A role inside a product the user cannot
    // open is a grant with nothing behind it.
    setProductRoles((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  /*
    AN ADMINISTRATOR'S ASSIGNMENT IS DERIVED, NOT STORED. Nothing here writes a seat row
    for every licensed module when the platform role is an administrator, for two reasons:
    the server already grants the access from the role alone, and a stored seat would be
    counted against the client's licence — charging them for access they get by virtue of
    administering the install.

    What is already stored stays stored: `modules` and `productRoles` are sent unchanged,
    so promoting someone to administrator never deletes the assignments they held as a
    Standard User, and demoting them hands those assignments back intact.
  */
  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      if (isNew) {
        await api.admin.createUser({
          fullName,
          email,
          role,
          password,
          ...(talogentAccount ? {} : { modules, grants: grantsFor(modules), productRoles }),
        });
      } else {
        await api.admin.updateUser(user.user_id, { fullName, email, role, isActive });
        await api.admin.setUserModules(user.user_id, modules, grantsFor(modules), productRoles);
      }
      onSaved();
    } catch (err) {
      const message = describeError(err);
      setError(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  };

  /** Every field is locked while not editing — the panel is a viewer first. */
  const ro = !editing;

  /*
    An ACTIVE administrator holds every licensed application with the Administrator role.
    Inactive is deliberately excluded: deactivating is how access is taken away, and an
    inactive account showing a full assignment strip would say the opposite.
  */
  const adminHoldsAll = ADMIN_ROLES.includes(role) && isActive;

  return (
    <Box
      component="aside"
      sx={{
        borderLeft: '1px solid #e0e6ea',
        // `bgcolor`, not `background`: only the former is palette-mapped by `sx`, and
        // `background: 'background.paper'` emits invalid CSS and leaves the panel clear.
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        // Same reason as the grid container: without this the panel cannot shrink to its
        // grid area, so its body never becomes the thing that scrolls.
        minHeight: 0,
        height: '100%',
      }}
    >
      {/*
        HEADER — the prototype's `.p-hd`: avatar, title 17/600, sub 12 #888, then the
        Application Master Data controls `‹ Back`, maximize and the RED close.

        This is inside the page, so nothing can paint over it. As a Drawer it was a modal
        underneath the shell's app bar, and the bar covered exactly this strip.
      */}
      <Box
        sx={{
          padding: '14px 16px',
          borderBottom: '1px solid #e0e6ea',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        {isNew ? null : <Avatar name={user.full_name} id={user.user_id} size={38} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isNew ? 'Add user' : user.full_name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#888', mt: '2px', wordBreak: 'break-all' }}>
            {isNew ? 'New account' : user.email}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Box component="button" type="button" onClick={onClose} sx={BACK_BUTTON_SX}>
            ‹ Back
          </Box>
          <Tooltip title={maximized ? 'Restore' : 'Maximize'}>
            <Box component="button" type="button" onClick={onToggleMaximized} sx={PANEL_ICON_SX}>
              {maximized ? (
                <CollapseIcon sx={{ fontSize: 19 }} />
              ) : (
                <ExpandIcon sx={{ fontSize: 19 }} />
              )}
            </Box>
          </Tooltip>
          <Tooltip title="Close">
            <Box component="button" type="button" onClick={onClose} sx={CLOSE_BUTTON_SX}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </Box>
          </Tooltip>
        </Stack>
      </Box>

      {/*
        VIEW MODE — the prototype's `.p-body`, exactly: an ACCOUNT key/value list over a
        MODULE ACCESS & ROLE strip. Edit swaps in the form below; the prototype's Edit
        button is inert, but this screen has to actually change things.
      */}
      {!editing && user ? (
        <Box sx={{ padding: '14px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Box sx={{ mb: '18px' }}>
            <Typography sx={SECTION_LABEL}>Account</Typography>
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: '118px minmax(0, 1fr)',
                gap: '6px 10px',
                fontSize: 13,
                m: 0,
              }}
            >
              <Box component="dt" sx={{ color: '#90a4ae' }}>
                Platform role
              </Box>
              <Box component="dd" sx={{ m: 0, color: '#455a64' }}>
                {user.role}
              </Box>
              <Box component="dt" sx={{ color: '#90a4ae' }}>
                Status
              </Box>
              <Box component="dd" sx={{ m: 0 }}>
                <Box
                  component="span"
                  sx={{ fontSize: 13, fontWeight: 600, color: STATUS_COLOR[statusOf(user)] }}
                >
                  {statusOf(user)[0]!.toUpperCase() + statusOf(user).slice(1)}
                </Box>
              </Box>
              <Box component="dt" sx={{ color: '#90a4ae' }}>
                Last sign-in
              </Box>
              <Box component="dd" sx={{ m: 0, color: '#455a64' }}>
                {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
              </Box>
              <Box component="dt" sx={{ color: '#90a4ae' }}>
                User ID
              </Box>
              <Box component="dd" sx={{ m: 0, color: '#455a64' }}>
                {user.user_id}
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography sx={SECTION_LABEL}>Module access &amp; role</Typography>
            {adminHoldsAll ? (
              <Typography sx={{ fontSize: 11.5, color: '#90a4ae', mb: 1 }}>
                Every licensed application, as {role} — automatic, not assigned by hand.
              </Typography>
            ) : null}
            {licensed.map((entitlement, i) => {
              const key = entitlement.module_key;
              const roles = rolesForModule(key);
              const current = user.productRoles?.[key];
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    padding: '9px 0',
                    borderTop: i === 0 ? 0 : '1px solid #eef2f6',
                  }}
                >
                  <ModBadge moduleKey={key} />
                  <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
                    {catalogName(key)}
                  </Box>
                  {adminHoldsAll ? (
                    <Box sx={{ fontSize: 12.5, color: '#1565c0', fontWeight: 600, textAlign: 'right' }}>
                      {IMPLIED_ADMIN_ROLE}
                    </Box>
                  ) : roles.length ? (
                    <Box sx={{ fontSize: 12.5, color: '#455a64', textAlign: 'right', maxWidth: 195 }}>
                      {roles.find((r) => r.role_key === current)?.role_name ?? '— none —'}
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: 12, color: '#90a4ae', fontStyle: 'italic' }}>
                      no roles defined
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
      <>
      {/*
        EDIT MODE, TWO REGIONS. The owner's final design (2026-08-08): "the content that
        should be scrolled should be only related to which role I can give to the client
        for which application."

        So who the person IS — name, email, platform role, active — stays PINNED under the
        header, and only the application/role list below it moves. With the footer pinned
        too, the panel has exactly one scrolling region and everything you act on stays
        where you left it.
      */}
      <Box sx={{ padding: '14px 16px 0', flexShrink: 0 }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            fullWidth
            disabled={ro}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            disabled={ro}
          />
          {talogentAccount ? (
            <TextField
              label="Platform role"
              value="Talogent Administrator"
              fullWidth
              disabled
              helperText="Talogent staff hold one role and reach every module."
            />
          ) : (
            <TextField
              label="Platform role"
              select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
              disabled={ro}
              helperText="Administrators can reach the Administration module."
            >
              {rolesFor(isTalogent).map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isNew ? (
            <TextField
              label="Initial password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              helperText="At least 12 characters with upper case, lower case and a number. The user must change it at first sign-in."
            />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  disabled={ro}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          )}

        </Stack>
      </Box>

      {/* The one scrolling region. */}
      <Box sx={{ padding: '14px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <Stack spacing={2.5}>
          {talogentAccount ? (
            <Alert severity="info">
              Talogent staff reach every module and are never counted against the
              client&apos;s licences, so there is no application assignment here.
            </Alert>
          ) : (
          <Box>
            <Typography variant="overline" color="text.secondary">
              Application access
            </Typography>
            {!adminHoldsAll ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Tick an application to give this person a licence for it.
              </Typography>
            ) : null}

            {/*
              ADMINISTRATORS ALREADY REACH EVERY LICENSED MODULE. Saying so here, because
              the tick-boxes below implied the opposite — an administrator with nothing
              ticked looked locked out of everything, and the owner read it that way.

              It is the server that decides this, in three separate places, and all three
              agree: `resolvePortalTiles` shows every licensed module to any client user
              (seats stopped gating visibility on 2026-08-03); `requireAprAccess` calls
              `next()` for an admin before it looks at `assignedModules`; and Inventory
              Registry and Project Management branch on their own `isAdmin` throughout.

              The ticks still do something real — they record and count SEATS, which the
              Entitlements screen enforces — so they stay editable rather than being
              force-ticked. What changes is that the screen no longer implies an
              administrator needs one.
            */}
            {/*
              NO EXPLANATORY BANNER HERE. The owner (2026-08-08): remove it and give the
              space back to the list. Every row already reads "Administrator" and every
              tick is already on and locked, so the banner was restating what the rows
              said while taking the vertical space they needed.
            */}

            {licensed.length === 0 ? (
              <Alert severity="warning">
                No modules are licensed. Enable one under Entitlements first.
              </Alert>
            ) : (
              /*
                ONE BLOCK PER APPLICATION, not a two-column grid.

                The owner's ruling (2026-08-08): "provide more space and keep alignment of
                application, module and icon with the checkbox; pull-down window can be
                below that… make enough space so it's not squashed."

                So each application is its own block with two lines:

                  ┌ tick · icon · name ─────────────────────── seats ┐
                  └        role dropdown, indented under the name    ┘

                The grid it replaced put the dropdown INSIDE the name cell, so a ticked row
                grew taller than an unticked one, the seat figure floated to the middle of
                whatever height that produced, and the whole strip lost its line. A block
                per application keeps the top line's alignment identical on every row no
                matter what sits underneath it.

                `ROW_INDENT` is the checkbox's own footprint, so the dropdown starts exactly
                under the icon rather than at a guessed offset.
              */
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 0.5,
                    pb: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, flex: 1 }}>
                    APPLICATION
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                    SEATS IN USE
                  </Typography>
                </Box>

                {licensed.map((entitlement) => {
                  const key = entitlement.module_key;
                  const checked = modules.includes(key);
                  // ONE KIND OF LICENCE. The owner's ruling (2026-08-07): "we don't need
                  // any distinction between permanent and temporary users anywhere… it's
                  // just one licence". So there is one pool, one seat count, and ticking
                  // the box is the whole decision.
                  const full =
                    entitlement.seats_available !== null && entitlement.seats_available <= 0;
                  const unavailable = !checked && full;

                  const pool = (used: number, limit: number) =>
                    limit >= UNLIMITED_SEATS ? `${used} / ∞` : `${used} / ${limit}`;

                  const ModuleIcon = getModuleIcon(catalogIcon(key));
                  const roles = rolesForModule(key);
                  const showRole = !adminHoldsAll && checked && roles.length > 0;

                  return (
                    <Box
                      key={key}
                      sx={{
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: '#f2f5f7',
                        '&:last-of-type': { borderBottom: 0 },
                      }}
                    >
                      {/* Line one — tick, icon, name, seats. Identical height on every row. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5, flexShrink: 0 }}
                          checked={adminHoldsAll ? true : checked}
                          disabled={ro || adminHoldsAll || unavailable}
                          onChange={() => toggleModule(key)}
                          inputProps={{ 'aria-label': catalogName(key) }}
                        />
                        <ModuleIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
                          {catalogName(key)}
                        </Typography>
                        {unavailable ? <StatusText label="No seats left" color="#d32f2f" /> : null}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, pl: 1 }}
                        >
                          {pool(entitlement.seats_used, entitlement.seat_limit)}
                        </Typography>
                      </Box>

                      {/*
                        Line two — the role INSIDE this product, indented under the icon.

                        Shown only once the product is ticked: a role is meaningless without
                        access to the thing it is a role in. "No role" is a real choice — a
                        user can hold a seat without being an analyst or an owner.
                      */}
                      {adminHoldsAll ? (
                        <Box sx={{ pl: ROW_INDENT, pt: 0.75, fontSize: 13, fontWeight: 600, color: '#1565c0' }}>
                          {IMPLIED_ADMIN_ROLE}
                        </Box>
                      ) : showRole ? (
                        <Box sx={{ pl: ROW_INDENT, pt: 1 }}>
                          <TextField
                            select
                            size="small"
                            disabled={ro}
                            fullWidth
                            label="Role in this application"
                            value={productRoles[key] ?? ''}
                            onChange={(e) =>
                              setProductRoles((current) => {
                                const next = { ...current };
                                if (e.target.value) next[key] = e.target.value;
                                else delete next[key];
                                return next;
                              })
                            }
                            sx={{ maxWidth: 340 }}
                          >
                            <MenuItem value="">
                              <em>No role</em>
                            </MenuItem>
                            {roles.map((r) => (
                              <MenuItem key={r.role_key} value={r.role_key}>
                                {r.role_name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          )}
        </Stack>
      </Box>
      </>
      )}

      {/*
        FOOTER — EDIT MODE ONLY, AND PINNED.

        VIEW CHANGES NOTHING. The owner's ruling (2026-08-08): opening a person with 👁️ is
        for reading them, so it offers no way to alter them — no Lock, no Deactivate, no
        Reset password, not even Edit. The panel has no footer at all in view mode.

        Every one of those four writes to the account, and three of them take effect
        immediately with no confirmation. A read-only view that carries them is one
        mis-click from a change nobody meant; 👁️ and ✏️ on the row are already the two
        doors, and they should lead somewhere different.

        In EDIT mode they are all present and pinned, with Save and Cancel above them, so
        nothing moves while the application list scrolls underneath. Edit greys out there,
        since it is already what you are doing.

        Delete is absent from both — it is a row action on the grid, so a destructive step
        is never adjacent to a routine one.
      */}
      {editing ? (
      <Box sx={{ borderTop: '1px solid #e0e6ea', padding: '12px 16px', flexShrink: 0 }}>
        {editing ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: user ? 1 : 0 }}>
            <Button onClick={isNew ? onClose : () => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || !fullName.trim() || !email.trim() || (isNew && !password)}
            >
              {saving ? 'Saving…' : isNew ? 'Create user' : 'Save changes'}
            </Button>
          </Stack>
        ) : null}

        {user ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button
              type="button"
              style={{ ...PANEL_BTN, ...(editing ? DISABLED_BTN : {}) }}
              disabled={editing}
              onClick={() => setEditing(true)}
            >
              <span>✏️</span> Edit
            </button>
            <button
              type="button"
              style={PANEL_BTN}
              disabled={busy}
              onClick={() => onToggleLock(user)}
            >
              <span>{user.is_locked === 1 ? '🔓' : '🔒'}</span>
              {user.is_locked === 1 ? 'Unlock' : 'Lock'}
            </button>
            <button
              type="button"
              style={PANEL_BTN}
              disabled={busy}
              onClick={() => onToggleActive(user)}
            >
              <span>{user.is_active === 1 ? '🚫' : '✅'}</span>
              {user.is_active === 1 ? 'Deactivate' : 'Activate'}
            </button>
            <button type="button" style={PANEL_BTN} onClick={() => onResetPassword(user)}>
              <span>🔑</span> Reset password
            </button>
          </Box>
        ) : null}
      </Box>
      ) : null}
    </Box>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await api.admin.resetPassword(user.user_id, password);
      onDone();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password for {user.full_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2" color="text.secondary">
            The account is unlocked and the user must choose a new password at next sign-in.
            Recently used passwords are rejected.
          </Typography>
          <TextField
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !password}>
          {saving ? 'Resetting…' : 'Reset password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
